import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import moment from 'moment-timezone';
import Nodata from '../../../components/common/NoData';
import Loader from '../../../components/common/Loader';
import { getAssignmentsForClassroom, addClassroomAssignment, deleteClassroomAssignment, updateClassroomAssignment } from '../../../services/adminService';
import { encodeData } from '../../../utils/encodeAndEncode';
import timeZones from '../../../utils/timeZone.json';
import { usePermissions } from '../../../context/PermissionsContext';
const DefaultTimeZone = process.env.REACT_APP_DEFAULT_TIMEZONE;

function ClassroomAssignment({decodedClassroomId}) {
  // console.log('decodedClassroomId-------------',decodedClassroomId);

  const navigate = useNavigate();
  const { permissions } = usePermissions();
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    points: '0',
    dueDate: '',
    timeZone: DefaultTimeZone, // Using DefaultTimeZone instead of browser's timezone
    visibility: 'show' // Default value
  });
  const [timeZoneSearch, setTimeZoneSearch] = useState('');
  const [isTimeZoneDropdownOpen, setIsTimeZoneDropdownOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  // Character limits
  const MAX_NAME_LENGTH = 100;
  const MAX_DESCRIPTION_LENGTH = 500;

  // Visibility options
  const visibilityOptions = [
    { value: 'show', label: 'Show' },
    { value: 'hide', label: 'Hide' }
  ];

  // Check if form has errors or required fields are empty
  const hasFormErrors = () => {
    return Object.values(formErrors).some(error => error) ||
           !formData.name.trim() ||
           !formData.dueDate ||
           !formData.visibility;
  };

  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 0
  });
  const [searchTerm, setSearchTerm] = useState('');

  // Delete modal states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchAssignments = async (page = 1, search = '') => {
    setLoading(true);
    try {
      const response = await getAssignmentsForClassroom({
        class_id: decodedClassroomId,
        page: page,
        limit: 10,
        search: search
      });

      // console.log('getAssignments response', response);

      if (response.success) {
        setAssignments(response.data.assignments || []);
        setPagination(response.data.pagination || {});
      }
    } catch (error) {
      // console.log('Error fetching assignments:', error);
      setAssignments([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchAssignments();
  }, [decodedClassroomId]);

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    fetchAssignments(1, value);
  };

  // Validation function
  const validateField = (name, value) => {
    const errors = {};

    switch (name) {
      case 'name':
        if (!value.trim()) {
          errors.name = 'Assignment name is required';
        } else if (value.length > MAX_NAME_LENGTH) {
          errors.name = `Assignment name cannot exceed ${MAX_NAME_LENGTH} characters`;
        }
        break;
      case 'description':
        if (value.length > MAX_DESCRIPTION_LENGTH) {
          errors.description = `Description cannot exceed ${MAX_DESCRIPTION_LENGTH} characters`;
        }
        break;
      case 'dueDate':
        if (!value) {
          errors.dueDate = 'Due date is required';
        } else {
          const selectedDate = new Date(value);
          const currentDate = new Date();
          if (selectedDate <= currentDate) {
            errors.dueDate = 'Due date must be in the future';
          }
        }
        break;
      case 'points':
        if (value && value < 0) {
          errors.points = 'Points cannot be negative';
        }
        break;
      case 'visibility':
        if (!value) {
          errors.visibility = 'Visibility is required';
        }
        break;
      default:
        break;
    }

    return errors;
  };

  const   handleInputChange = (e) => {
    const { name, value } = e.target;

    // Update form data
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Real-time validation
    const fieldErrors = validateField(name, value);
    setFormErrors(prev => ({
      ...prev,
      [name]: fieldErrors[name] || null
    }));
  };

  const handleModalClose = () => {
    setShowModal(false);
    setIsSubmitting(false);
    // Reset form
    setFormData({
      name: '',
      description: '',
      points: '0',
      dueDate: '',
      visibility: 'show'
    });
    // Reset form errors
    setFormErrors({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!permissions.classroom_assignment_create) {
      toast.warning("You don't have permission to create assignments");
      return;
    }

    // Validate all fields
    const nameErrors = validateField('name', formData.name);
    const descriptionErrors = validateField('description', formData.description);
    const dueDateErrors = validateField('dueDate', formData.dueDate);
    const visibilityErrors = validateField('visibility', formData.visibility);

    const allErrors = { ...nameErrors, ...descriptionErrors, ...dueDateErrors, ...visibilityErrors };

    if (Object.keys(allErrors).length > 0) {
      setFormErrors(allErrors);
      // Show first error in toast
      const firstError = Object.values(allErrors)[0];
      toast.error(firstError);
      return;
    }

    setIsSubmitting(true);

    try {
      // Convert the local date to UTC based on selected timezone
      const localDate = moment.tz(formData.dueDate, formData.timeZone);
      const utcDate = localDate.utc().format();


      const payload = {
        class_id: decodedClassroomId,
        title: formData.name.trim(),
        description: formData.description.trim() || '',
        earning_points: formData.points || 0,
        due_date: utcDate, // Using the UTC converted date
        is_visible: formData.visibility === 'show' ? 1 : 0,
        total_marks: 0,
        time_zone: formData.timeZone // Adding the selected timezone
      };

      // console.log('Submitting assignment:', payload);

      const response = await addClassroomAssignment(payload);

      // console.log('Add assignment response:', response);

      if (response.success) {
        toast.success('Assignment created successfully!');
        handleModalClose();
        fetchAssignments(1, searchTerm);
      } else {
        toast.error(response.message || 'Failed to create assignment');
      }
    } catch (error) {
      console.error('Error creating assignment:', error);
      toast.error('An error occurred while creating the assignment');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Update assignment function
  const handleUpdateAssignment = async (formData) => {
    try {
      // Log form data for debugging
      for (let [key, value] of formData.entries()) {
        // console.log(`${key}:`, value);
      }

      // Call the API to update the assignment
      const response = await updateClassroomAssignment(formData);
      // console.log("Update assignment response:", response);

      // Check if response exists and has expected properties
      if (response) {
        // Refresh the assignments list with current search and pagination
        await fetchAssignments(1, searchTerm);
        setShowEditModal(false);
        toast.success(response.message || "Assignment updated successfully");
        return response;
      } else {
        // Handle error case
        const errorMsg = response?.message || "Failed to update assignment";
        toast.error(errorMsg);
        throw new Error(errorMsg);
      }
    } catch (error) {
      console.error("Error updating assignment:", error);
      toast.error(error.message || "An error occurred while updating the assignment");
      throw error; // Re-throw to allow the calling component to handle it
    }
  };

  // Handle edit assignment
  const handleEditAssignment = (assignment) => {
    if (!permissions.classroom_assignment_edit) {
      toast.warning("You don't have permission to edit assignments");
      return;
    }
    setEditingAssignment(assignment);
    
    // Convert UTC date to local date in the assignment's timezone
    const utcDate = moment.utc(assignment.due_date);
    const localDate = utcDate.tz(assignment.time_zone || DefaultTimeZone);
    
    setFormData({
      name: assignment.title,
      description: assignment.description || '',
      points: assignment.earning_points !== undefined && assignment.earning_points !== null ? assignment.earning_points.toString() : '',
      dueDate: localDate.format('YYYY-MM-DDTHH:mm'), // Format for datetime-local input
      timeZone: assignment.time_zone || DefaultTimeZone,
      visibility: assignment.is_visible === 1 ? 'show' : 'hide'
    });
    setFormErrors({});
    setShowEditModal(true);
  };

  // Handle edit form submission
  const handleEditSubmit = async (e) => {
    e.preventDefault();

    // Validate all fields
    const nameErrors = validateField('name', formData.name);
    const descriptionErrors = validateField('description', formData.description);
    const dueDateErrors = validateField('dueDate', formData.dueDate);
    const visibilityErrors = validateField('visibility', formData.visibility);

    const allErrors = { ...nameErrors, ...descriptionErrors, ...dueDateErrors, ...visibilityErrors };

    if (Object.keys(allErrors).length > 0) {
      setFormErrors(allErrors);
      // Show first error in toast
      const firstError = Object.values(allErrors)[0];
      toast.error(firstError);
      return;
    }

    setIsSubmitting(true);

    try {
      // Convert the local date to UTC based on selected timezone
      const localDate = moment.tz(formData.dueDate, formData.timeZone);
      const utcDate = localDate.utc().format();

      const payload = {
        assignment_id: editingAssignment.id,
        class_id: decodedClassroomId,
        title: formData.name.trim(),
        description: formData.description.trim() || '',
        earning_points: formData.points || 0,
        due_date: utcDate,
        is_visible: formData.visibility === 'show' ? 1 : 0,
        total_marks: editingAssignment.total_marks || 0,
        time_zone: formData.timeZone
      };

      // console.log('Updating assignment:', payload);

      const response = await updateClassroomAssignment(payload);

      // console.log('Update assignment response:', response);

      if (response.success) {
        toast.success('Assignment updated successfully!');
        handleEditModalClose();
        fetchAssignments(1, searchTerm);
      } else {
        toast.error(response.message || 'Failed to update assignment');
      }
    } catch (error) {
      console.error('Error updating assignment:', error);
      toast.error('An error occurred while updating the assignment');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle edit modal close
  const handleEditModalClose = () => {
    setShowEditModal(false);
    setEditingAssignment(null);
    setIsSubmitting(false);
    // Reset form
    setFormData({
      name: '',
      description: '',
      points: '0',
      dueDate: '',
      visibility: 'show'
    });
    // Reset form errors
    setFormErrors({});
  };

  // Handle delete assignment
  const handleDeleteAssignment = (assignment) => {
    if (!permissions.classroom_assignment_delete) {
      toast.warning("You don't have permission to delete assignments");
      return;
    }
    setSelectedAssignment(assignment);
    setShowDeleteModal(true);
  };

  // Handle delete confirmation
  const deleteClassAssignment = async () => {
    if (selectedAssignment) {
      setIsDeleting(true);
      try {
        // Call the API to delete the assignment
        const response = await deleteClassroomAssignment({ assignment_id: selectedAssignment.id });

        if (response.success) {
          // Close the delete confirmation popup
          setShowDeleteModal(false);
          setSelectedAssignment(null);

          // Refresh the assignments list with current search and pagination
          await fetchAssignments(1, searchTerm);
          toast.success("Assignment deleted successfully");
        } else {
          toast.error(response.message || 'Failed to delete assignment');
        }
      } catch (error) {
        console.error('Error deleting assignment:', error);
        toast.error('An error occurred while deleting the assignment');
      } finally {
        setIsDeleting(false);
      }
    }
  };

  // Handle delete modal close
  const handleDeleteModalClose = () => {
    setShowDeleteModal(false);
    setSelectedAssignment(null);
  };

  const HandleAssignmentDetails = (assignmentId) => {
    if (!permissions.classroom_assignment_manage) {
      toast.warning("You don't have permission to manage assignments");
      return;
    }
    // Encode both classroom_id and assignment_id
    const encodedClassroomId = encodeData(decodedClassroomId);
    const encodedAssignmentId = encodeData(assignmentId);
    
    navigate(`/admin/live-sessions/classroom-dashboard/${encodedClassroomId}/assingment/${encodedAssignmentId}/ClassroomAssignmentDetails`);
    // Navigate with both encoded IDs
    // navigate(`/admin/ClassroomAssignmentDetails/${encodedClassroomId}/${encodedAssignmentId}`);
  }

  // Add this helper function at the top level of your component
  const getAssignmentStatus = (dueDate) => {
    const now = new Date();
    const due = new Date(dueDate);
    
    if (due < now) {
      return { status: 'ended', className: 'text-red-500' };
    }
    return { status: 'ongoing', className: 'text-green-500' };
  };

  const totalRecords = pagination.total ?? 0;
  const totalPages = pagination.totalPages ?? 1;
  const page = pagination.page ?? 1;
  const limit = pagination.limit ?? 10;
  const startRecord = totalRecords === 0 ? 0 : (page - 1) * limit + 1;
  const endRecord = Math.min(page * limit, totalRecords);

  return (
    <React.Fragment>
      <div className="overflow-hidden">
        <div className="flex flex-nowrap items-center gap-3 px-0 py-0 border-b-0 bg-transparent overflow-x-auto whitespace-nowrap">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Icon icon="fluent:search-20-regular" className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="search"
              className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#4F6BFF]/20 focus:border-[#4F6BFF]"
              placeholder="Search assignments..."
              value={searchTerm}
              onChange={handleSearch}
              aria-label="Search assignments"
            />
          </div>
          <button
            type="button"
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-[#4F6BFF] hover:bg-[#4358d4] text-white font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
            onClick={() => setShowModal(true)}
            disabled={!permissions.classroom_assignment_create}
            title={!permissions.classroom_assignment_create ? "You don't have permission to create assignments" : "Add Assignment"}
          >
            <Icon icon="fluent:add-24-regular" width="18" height="18" className="shrink-0" />
            <span>Add Assignment</span>
          </button>
        </div>

        <div className="p-0 mt-4">
      {loading ? (
        <div className="flex justify-center py-16">
          <Loader caption="Loading assignments..." />
        </div>
      ) : assignments.length > 0 ? (
        <div className="space-y-4">
        {assignments.map((assignment) => {
          const dueStatus = getAssignmentStatus(assignment.due_date);
          
          return (
            <div key={assignment.id} className="rounded-xl border border-gray-100 bg-white overflow-hidden hover:border-gray-200 transition-colors">
                <div className="p-4 sm:p-5">
                  {/* title and icons */}
                  <div className="w-full flex justify-between items-center mb-3">
                    <h5 className="font-semibold text-lg mb-0">{assignment.title}</h5>
                    <div className="flex gap-2 items-center">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium ${
                        assignment.is_visible ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-amber-50 text-amber-700 border border-amber-200'
                      }`}>
                        {assignment.is_visible ? 'Visible' : 'Hidden'}
                      </span>
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium ${
                        moment.utc(assignment.due_date).local().isBefore(moment()) 
                          ? 'bg-red-50 text-red-700 border border-red-200' 
                          : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                      }`}>
                        {moment.utc(assignment.due_date).local().isBefore(moment()) ? (
                          'Assignment Ended'
                        ) : (
                          <div className="flex items-center gap-2">
                            Assignment Ongoing
                            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                          </div>
                        )}
                      </span>

                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          className="inline-flex items-center justify-center shrink-0 w-9 h-9 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                          title={!permissions.classroom_assignment_edit ? "You don't have permission to edit assignments" : "Edit"}
                          onClick={() => handleEditAssignment(assignment)}
                          disabled={!permissions.classroom_assignment_edit}
                        >
                          <Icon icon="fluent:edit-24-regular" width="18" height="18" />
                        </button>
                        <button
                          type="button"
                          className="inline-flex items-center justify-center shrink-0 w-9 h-9 rounded-lg border border-red-500 text-red-500 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          title={!permissions.classroom_assignment_delete ? "You don't have permission to delete assignments" : "Delete"}
                          onClick={() => handleDeleteAssignment(assignment)}
                          disabled={!permissions.classroom_assignment_delete}
                        >
                          <Icon icon="fluent:delete-24-regular" width="18" height="18" />
                        </button>
                      </div>
                    </div>
                  </div>
                  <hr className="my-3 border-gray-200" />

                  {/* description */}
                  <div className="w-full mb-3">
                    <div className="flex items-start">
                      <Icon icon="fluent:text-description-24-regular" className="text-primary mr-2 mt-1" width="20" height="20" />
                      <p className="mb-0" style={{ color: '#6c757d', fontSize: '14px' }}>{assignment.description}</p>
                    </div>
                  </div>

                  {/* card details */}
                  <div className="w-full mb-3">
                    <div className="flex">
                      <div className="md:w-1/2">
                        <div className="flex items-center mb-2">
                          <Icon icon="fluent:calendar-24-regular" className="text-primary mr-2" width="20" height="20" />
                          <span>
  Due: {moment.utc(assignment.due_date).local().format('MMM DD, YYYY, hh:mm A')}
  <span className={moment.utc(assignment.due_date).local().isBefore(moment()) ? 'text-red-500' : 'text-green-500'}>
    ({moment.utc(assignment.due_date).local().isBefore(moment()) ? 'Ended' : 'Ongoing'})
  </span>
</span>

                        </div>
                      </div>
                      <div className="md:w-1/2">
                        <div className="flex items-center mb-2">
                          <Icon icon="fluent:person-24-regular" className="text-primary mr-2" width="20" height="20" />
                          <span>Created by: {assignment.creator_name}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex">
                      <div className="md:w-1/2">
                        <div className="flex items-center">
                          <Icon icon="fluent:calendar-add-24-regular" className="text-primary mr-2" width="20" height="20" />
                          <span>Assigned: {new Date(assignment.assigned_date).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: '2-digit'
                          })}</span>
                        </div>
                      </div>
                      <div className="md:w-1/2">
                        <div className="flex items-center">
                          <Icon icon="fluent:trophy-24-regular" className="text-primary mr-2" width="20" height="20" />
                          <span>Total Marks: {assignment.total_marks || 'Not set'}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* buttons manage and trainees */}
                  <div className="w-full flex justify-end pt-2">
                    <button
                      type="button"
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#4F6BFF] hover:bg-[#4358d4] text-white font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={() => HandleAssignmentDetails(assignment.id)}
                      disabled={!permissions.classroom_assignment_manage}
                      title={!permissions.classroom_assignment_manage ? "You don't have permission to manage assignments" : "Manage Assignment"}
                    >
                      <Icon icon="fluent:settings-24-regular" width="18" height="18" />
                      Manage
                    </button>
                  </div>
                </div>
            </div>
          );
        })}
        </div>
      ) : (
        <div className="flex justify-center py-16">
          <Nodata message="No assignments found" />
        </div>
      )}

      {!loading && assignments.length > 0 && totalPages > 0 && (
        <div className="mt-6 pt-4 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3 text-sm text-gray-600">
            <span className="text-gray-500">
              {totalRecords === 0 ? 'No records' : `Showing ${startRecord}–${endRecord} of ${totalRecords}`}
            </span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <button
              type="button"
              className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => fetchAssignments(page - 1, searchTerm)}
              disabled={page === 1 || totalPages === 0}
              title="Previous page"
            >
              <Icon icon="fluent:chevron-left-24-regular" width={16} height={16} />
            </button>
            <span className="px-2 text-gray-700">
              Page <span className="font-medium">{totalPages === 0 ? 0 : page}</span> of <span className="font-medium">{totalPages || 0}</span>
            </span>
            <button
              type="button"
              className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => fetchAssignments(page + 1, searchTerm)}
              disabled={page === totalPages || totalPages === 0}
              title="Next page"
            >
              <Icon icon="fluent:chevron-right-24-regular" width={16} height={16} />
            </button>
          </div>
        </div>
      )}
        </div>
      </div>

      {/* Add Assignment Modal */}
      {showModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
          aria-modal="true"
          role="dialog"
        >
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full">
            <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center">
              <h5 className="font-semibold text-lg text-gray-900">Add Assignment</h5>
              <button
                type="button"
                className="p-1.5 rounded-full hover:bg-gray-100 text-gray-500"
                onClick={handleModalClose}
                aria-label="Close"
              >
                <Icon icon="mdi:close" width="20" height="20" />
              </button>
            </div>

            <div className="p-6">
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Name */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      className={`w-full px-3 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 ${
                        formErrors.name ? 'border-red-500' : 'border-input-border'
                      }`}
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      maxLength={MAX_NAME_LENGTH}
                      placeholder="Enter assignment name"
                    />
                    {formErrors.name ? (
                      <p className="text-red-500 text-xs mt-1">{formErrors.name}</p>
                    ) : (
                      <p className="text-gray-500 text-xs mt-1">
                        Maximum {MAX_NAME_LENGTH} characters ({MAX_NAME_LENGTH - formData.name.length} remaining)
                      </p>
                    )}
                  </div>

                  {/* Description */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Description
                    </label>
                    <textarea
                      className={`w-full px-3 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 ${
                        formErrors.description ? 'border-red-500' : 'border-input-border'
                      }`}
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      maxLength={MAX_DESCRIPTION_LENGTH}
                      rows={4}
                      placeholder="Describe what trainees need to do"
                    />
                    {formErrors.description ? (
                      <p className="text-red-500 text-xs mt-1">{formErrors.description}</p>
                    ) : (
                      <p className="text-gray-500 text-xs mt-1">
                        Maximum {MAX_DESCRIPTION_LENGTH} characters ({MAX_DESCRIPTION_LENGTH - formData.description.length} remaining)
                      </p>
                    )}
                  </div>

                  {/* Points */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Points
                    </label>
                    <input
                      type="number"
                      className={`w-full px-3 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 ${
                        formErrors.points ? 'border-red-500' : 'border-input-border'
                      }`}
                      name="points"
                      value={formData.points}
                      onChange={handleInputChange}
                      placeholder="0"
                      min="0"
                    />
                    <p className="text-gray-500 text-xs mt-1">
                      Reward points trainees receive after completing this assignment.
                    </p>
                    {formErrors.points && (
                      <p className="text-red-500 text-xs mt-1">{formErrors.points}</p>
                    )}
                  </div>

                  {/* Visibility */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Visibility <span className="text-red-500">*</span>
                    </label>
                    <select
                      className={`w-full px-3 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 ${
                        formErrors.visibility ? 'border-red-500' : 'border-input-border'
                      }`}
                      name="visibility"
                      value={formData.visibility}
                      onChange={handleInputChange}
                    >
                      {visibilityOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    {formErrors.visibility && (
                      <p className="text-red-500 text-xs mt-1">{formErrors.visibility}</p>
                    )}
                  </div>

                  {/* Due date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Due date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="datetime-local"
                      className={`w-full px-3 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 ${
                        formErrors.dueDate ? 'border-red-500' : 'border-input-border'
                      }`}
                      name="dueDate"
                      value={formData.dueDate}
                      onChange={handleInputChange}
                      min={new Date().toISOString().slice(0, 16)}
                    />
                    {formErrors.dueDate && (
                      <p className="text-red-500 text-xs mt-1">{formErrors.dueDate}</p>
                    )}
                  </div>

                  {/* Time zone */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Time zone <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <button
                        type="button"
                        className={`w-full px-3 py-2.5 rounded-xl border text-sm flex items-center justify-between text-left focus:outline-none focus:ring-2 focus:ring-primary/20 ${
                          formErrors.timeZone ? 'border-red-500' : 'border-input-border'
                        }`}
                        onClick={() => setIsTimeZoneDropdownOpen(!isTimeZoneDropdownOpen)}
                      >
                        <span className={formData.timeZone ? 'text-gray-900' : 'text-gray-400'}>
                          {formData.timeZone
                            ? timeZones.find((zone) => zone.value === formData.timeZone)?.label
                            : 'Select time zone'}
                        </span>
                        <Icon
                          icon={isTimeZoneDropdownOpen ? 'eva:arrow-up-fill' : 'eva:arrow-down-fill'}
                          className="ml-2 text-gray-500"
                        />
                      </button>

                      {isTimeZoneDropdownOpen && (
                        <div className="absolute z-50 mt-1 w-full rounded-xl border border-gray-200 bg-white shadow-lg">
                          <div className="p-2 border-b border-gray-100 bg-white sticky top-0 z-10">
                            <div className="relative">
                              <input
                                type="text"
                                className="w-full px-3 py-2 pl-9 rounded-lg border border-input-border text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
                                placeholder="Search time zone..."
                                value={timeZoneSearch}
                                onChange={(e) => setTimeZoneSearch(e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                              />
                              <Icon
                                icon="eva:search-outline"
                                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"
                                width="16"
                                height="16"
                              />
                            </div>
                          </div>

                          <div className="max-h-72 overflow-y-auto py-1">
                            {timeZones
                              .filter(
                                (zone) =>
                                  zone.label.toLowerCase().includes(timeZoneSearch.toLowerCase()) ||
                                  zone.value.toLowerCase().includes(timeZoneSearch.toLowerCase()),
                              )
                              .map((zone) => {
                                const isSelected = formData.timeZone === zone.value;
                                return (
                                  <button
                                    type="button"
                                    key={zone.value}
                                    className={`w-full text-left px-4 py-2.5 text-sm ${
                                      isSelected
                                        ? 'bg-primary/5 text-primary font-medium'
                                        : 'hover:bg-gray-50 text-gray-800'
                                    }`}
                                    onClick={() => {
                                      setFormData((prev) => ({ ...prev, timeZone: zone.value }));
                                      setIsTimeZoneDropdownOpen(false);
                                      setTimeZoneSearch('');
                                    }}
                                  >
                                    {zone.label}
                                  </button>
                                );
                              })}
                          </div>
                        </div>
                      )}
                    </div>
                    {formErrors.timeZone && (
                      <p className="text-red-500 text-xs mt-1">{formErrors.timeZone}</p>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-2">
                  <button
                    type="button"
                    className="inline-flex items-center justify-center px-4 py-2.5 rounded-xl border border-gray-300 bg-gray-50 text-gray-700 text-sm font-medium hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={handleModalClose}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="inline-flex items-center justify-center px-4 py-2.5 rounded-xl bg-primary hover:bg-primary/90 text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={isSubmitting || hasFormErrors()}
                  >
                    {isSubmitting ? (
                      <>
                        <span
                          className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Creating...
                      </>
                    ) : (
                      'Save Assignment'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Assignment Modal */}
      {showEditModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center">
              <h5 className="font-semibold text-lg">Edit Assignment</h5>
              <button
                type="button"
                className="p-1 rounded hover:bg-gray-100"
                onClick={handleEditModalClose}
              >
                <Icon icon="mdi:close" width="20" height="20" />
              </button>
            </div>
            <div className="p-6">
                {/* Name field */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 ${
                      formErrors.name ? 'border-red-500' : 'border-input-border'
                    }`}
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    maxLength={MAX_NAME_LENGTH}
                    placeholder="Enter assignment name"
                  />
                  {formErrors.name ? (
                    <div className="text-red-500 text-sm mt-1">{formErrors.name}</div>
                  ) : (
                    <small className="text-gray-500 text-sm mt-1 block">
                      Maximum {MAX_NAME_LENGTH} characters allowed ({MAX_NAME_LENGTH - formData.name.length} remaining)
                    </small>
                  )}
                </div>

                {/* Description field */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Description
                  </label>
                  <textarea
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 ${
                      formErrors.description ? 'border-red-500' : 'border-input-border'
                    }`}
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    maxLength={MAX_DESCRIPTION_LENGTH}
                    rows="4"
                    placeholder="Enter assignment description"
                  />
                  {formErrors.description ? (
                    <div className="text-red-500 text-sm mt-1">{formErrors.description}</div>
                  ) : (
                    <small className="text-gray-500 text-sm mt-1 block">
                      Maximum {MAX_DESCRIPTION_LENGTH} characters allowed ({MAX_DESCRIPTION_LENGTH - formData.description.length} remaining)
                    </small>
                  )}
                </div>

                {/* Points field */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Points
                  </label>
                  <input
                    type="number"
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 ${
                      formErrors.points ? 'border-red-500' : 'border-input-border'
                    }`}
                    name="points"
                    value={formData.points}
                    onChange={handleInputChange}
                    placeholder="Enter points"
                    min="0"
                  />
                  <small className="text-gray-500 text-sm mt-1 block">
                    Trainees will get these points once they complete this assignment
                  </small>
                  {formErrors.points && (
                    <div className="text-red-500 text-sm mt-1">{formErrors.points}</div>
                  )}
                </div>

                {/* Time Zone field */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Time Zone <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div 
                      className={`w-full px-3 py-2 border rounded-lg flex items-center justify-between cursor-pointer ${
                        formErrors.timeZone ? 'border-red-500' : 'border-input-border'
                      }`}
                      onClick={() => setIsTimeZoneDropdownOpen(!isTimeZoneDropdownOpen)}
                    >
                      <span>{formData.timeZone ? timeZones.find(zone => zone.value === formData.timeZone)?.label : 'Select Time Zone'}</span>
                      <Icon icon={isTimeZoneDropdownOpen ? "eva:arrow-up-fill" : "eva:arrow-down-fill"} />
                    </div>
                    {isTimeZoneDropdownOpen && (
                      <div 
                        className="absolute w-full bg-white border rounded-xl mt-1 shadow-sm" 
                        style={{ 
                          maxHeight: '300px',
                          overflowY: 'auto',
                          zIndex: 1000
                        }}
                      >
                        <div className="p-2 sticky top-0 bg-white">
                          <div className="relative">
                            <input
                              type="text"
                              className="w-full px-3 py-2 border border-input-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 pl-10"
                              placeholder="Search timezone..."
                              value={timeZoneSearch}
                              onChange={(e) => setTimeZoneSearch(e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                            />
                            <Icon 
                              icon="eva:search-outline"
                              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                            />
                          </div>
                        </div>
                        <div className="timezone-options">
                          {timeZones
                            .filter(zone => 
                              zone.label.toLowerCase().includes(timeZoneSearch.toLowerCase()) ||
                              zone.value.toLowerCase().includes(timeZoneSearch.toLowerCase())
                            )
                            .map((zone) => (
                              <div
                                key={zone.value}
                                className="timezone-option"
                                onClick={() => {
                                  setFormData(prev => ({ ...prev, timeZone: zone.value }));
                                  setIsTimeZoneDropdownOpen(false);
                                  setTimeZoneSearch('');
                                }}
                                style={{ 
                                  padding: '10px 16px',
                                  cursor: 'pointer',
                                  backgroundColor: formData.timeZone === zone.value ? '#f8f9fa' : 'transparent',
                                  transition: 'background-color 0.2s',
                                  fontSize: '0.875rem',
                                  color: '#333'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = formData.timeZone === zone.value ? '#f8f9fa' : 'transparent'}
                              >
                                {zone.label}
                              </div>
                            ))
                          }
                        </div>
                      </div>
                    )}
                  </div>
                  {formErrors.timeZone && (
                    <div className="text-red-500 text-sm mt-1">{formErrors.timeZone}</div>
                  )}
                </div>

                {/* Due Date field */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Due Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="datetime-local"
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 ${
                      formErrors.dueDate ? 'border-red-500' : 'border-input-border'
                    }`}
                    name="dueDate"
                    value={formData.dueDate}
                    onChange={handleInputChange}
                    min={moment().tz(formData.timeZone).format('YYYY-MM-DDTHH:mm')}
                  />
                  <small className="text-gray-500 text-sm mt-1 block">
                    Time shown in {timeZones.find(zone => zone.value === formData.timeZone)?.label || formData.timeZone}
                  </small>
                  {formErrors.dueDate && (
                    <div className="text-red-500 text-sm mt-1">{formErrors.dueDate}</div>
                  )}
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Visibility <span className="text-red-500">*</span>
                  </label>
                  <select
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 ${
                      formErrors.visibility ? 'border-red-500' : 'border-input-border'
                    }`}
                    name="visibility"
                    value={formData.visibility}
                    onChange={handleInputChange}
                  >
                    {visibilityOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  {formErrors.visibility && (
                    <div className="text-red-500 text-sm mt-1">{formErrors.visibility}</div>
                  )}
                </div>
              </div>
              <div className="px-6 py-4 border-t border-gray-100">
                <div className="flex flex-col gap-2 w-full">
                  <button
                    type="button"
                    className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 px-4 rounded-lg border border-gray-300"
                    onClick={handleEditModalClose}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="w-full bg-primary hover:bg-primary/90 text-white font-medium py-2 px-4 rounded-lg"
                    onClick={handleEditSubmit}
                    disabled={isSubmitting || hasFormErrors()}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" role="status" aria-hidden="true"></span>
                        Updating...
                      </>
                    ) : (
                      'Update Assignment'
                    )}
                  </button>
                </div>
              </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center">
              <h5 className="font-semibold text-lg text-red-500">Confirm Deletion</h5>
              <button
                type="button"
                className="p-1 rounded hover:bg-gray-100"
                onClick={handleDeleteModalClose}
              >
                <Icon icon="mdi:close" width="20" height="20" />
              </button>
            </div>
            <div className="p-6">
              <p className="text-gray-700 text-sm mb-4">
                Are you sure you want to delete the assignment <strong>"{selectedAssignment?.title}"</strong>?
              </p>
              <p className="text-red-500 text-sm font-medium">
                This action cannot be undone.
              </p>
            </div>
            <div className="px-6 py-4 border-t border-gray-100">
              <div className="flex flex-col gap-2 w-full">
                <button
                  type="button"
                  className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 px-4 rounded-lg border border-gray-300"
                  onClick={handleDeleteModalClose}
                  disabled={isDeleting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="w-full bg-red-500 hover:bg-red-600 text-white font-medium py-2 px-4 rounded-lg"
                  onClick={deleteClassAssignment}
                  disabled={isDeleting}
                >
                  {isDeleting ? (
                    <>
                      <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" role="status" aria-hidden="true"></span>
                      Deleting...
                    </>
                  ) : (
                    'Delete Assignment'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </React.Fragment>
  );
}

export default ClassroomAssignment
